import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';
import { match } from 'path-to-regexp';

import { Loading } from '@autopass-web/mochi-component';

import { EntryPage } from '../pages/entry';
import { LicensePlatePage } from '../pages/license-plate';
import { LoginPage } from '../pages/login';
import { PaymentPage } from '../pages/payment-page';
import { Success } from '../pages/success';
import { useLicensePlates } from '../services/use-license-plates';
import { useUserMe } from '../services/use-user-me';

import type { ReactNode } from 'react';
import type { NavigateProps, Path } from 'react-router-dom';

export const PATHS = {
  NOTFOUND: 'notfound',
  USER: 'user',
  PLATE: 'user/plate',
  RECEIPT: 'user/receipt',
  PAYMENT: 'user/payment',
  SUCCESS: 'user/success',
  FAIL: 'error',
} as const;

type Props = {
  children: ReactNode;
  exact?: boolean;
} & Omit<NavigateProps, 'to'> & { to: Partial<Path> };

const AssuredNavigate = ({ to, children, exact = false }: Props) => {
  const location = useLocation();

  const { pathname } = location;

  const matcherSuffix = exact ? '' : '{/*any}';

  const isMatch = !!match(to.pathname + matcherSuffix)(pathname);

  return isMatch ? (
    children
  ) : (
    <Navigate to={{ ...to, search: location.search }} />
  );
};

const User = () => {
  const { userMe, isPending } = useUserMe();

  const licensePlatesQuery = useLicensePlates({ enabled: !!userMe });

  const { licensePlates } = licensePlatesQuery;

  const children = <Outlet />;

  if (isPending) {
    return <Loading />;
  }

  if (!userMe) {
    return (
      <AssuredNavigate
        to={{ pathname: `/${PATHS.USER}` }}
        children={children}
        exact
      />
    );
  }

  if (licensePlatesQuery.isPending) {
    return <Loading />;
  }

  if ((licensePlates ?? []).length === 0) {
    return (
      <AssuredNavigate
        to={{ pathname: `/${PATHS.PLATE}` }}
        children={children}
      />
    );
  }

  if (!userMe.current_credit_card) {
    return (
      <AssuredNavigate
        to={{ pathname: `/${PATHS.PAYMENT}` }}
        children={children}
      />
    );
  }

  // if all complete
  return (
    <AssuredNavigate
      to={{ pathname: `/${PATHS.SUCCESS}` }}
      children={children}
    />
  );
};

const FallbackRedirect = () => {
  const params = useParams();

  return (
    <Navigate
      to={{
        pathname: '/user',
        search: `p=${params.p}&c=${params.c}`,
      }}
    />
  );
};

export const AppRoutes = () => {
  return (
    <Routes>
      <Route path={'/'}>
        <Route path={'user'} element={<User />}>
          <Route path={'success'} element={<Success />} />
          <Route path={'payment'} element={<PaymentPage />} />
          <Route path={'plate'} element={<LicensePlatePage />} />
          <Route index element={<LoginPage />} />
        </Route>
        <Route path={'p/:p/c/:c'} element={<FallbackRedirect />} />
        <Route index element={<EntryPage />} />
        <Route path={'*'} element={<Navigate to={{ pathname: '/user' }} />} />
      </Route>
    </Routes>
  );
};
